import { IEmailProvider, EmailProviderType } from './types';
import { EmailJSProvider } from './providers/EmailJSProvider';
import { VercelAPIProvider } from './providers/VercelAPIProvider';
import { CustomBackendProvider } from './providers/CustomBackendProvider';
import { FormSpreeProvider } from './providers/FormSpreeProvider';
import { Web3FormsProvider } from './providers/Web3FormsProvider';

/**
 * Email Service Factory - Patrón Strategy
 * 
 * Cambia el proveedor modificando la variable de entorno:
 * REACT_APP_EMAIL_PROVIDER=emailjs|vercel|custom|formspree|web3forms
 * 
 * Por defecto usa 'vercel' (recomendado para despliegue en Vercel)
 */
class EmailServiceFactory {
  private provider: IEmailProvider | null = null;
  private currentProviderType: EmailProviderType;

  constructor() {
    const envProvider = process.env.REACT_APP_EMAIL_PROVIDER as EmailProviderType;
    this.currentProviderType = envProvider || EmailProviderType.VERCEL_API;
  }

  /**
   * Obtiene el proveedor configurado
   */
  getProvider(): IEmailProvider {
    if (!this.provider) {
      this.provider = this.createProvider(this.currentProviderType);
    }
    return this.provider;
  }

  /**
   * Cambia el proveedor en runtime (opcional)
   */
  setProvider(providerType: EmailProviderType): void {
    this.currentProviderType = providerType;
    this.provider = this.createProvider(providerType);
  }

  /**
   * Crea una instancia del proveedor según el tipo
   */
  private createProvider(type: EmailProviderType): IEmailProvider {
    switch (type) {
      case EmailProviderType.EMAILJS:
        return new EmailJSProvider();
      
      case EmailProviderType.VERCEL_API:
        return new VercelAPIProvider();
      
      case EmailProviderType.CUSTOM_BACKEND:
        return new CustomBackendProvider();
      
      case EmailProviderType.FORMSPREE:
        return new FormSpreeProvider();
      
      case EmailProviderType.WEB3FORMS:
        return new Web3FormsProvider();
      
      default:
        console.warn(`Unknown provider type: ${type}, falling back to Vercel API`);
        return new VercelAPIProvider();
    }
  }

  /**
   * Obtiene información sobre el proveedor actual
   */
  getCurrentProviderInfo(): { type: EmailProviderType; name: string } {
    const names: Record<EmailProviderType, string> = {
      [EmailProviderType.EMAILJS]: 'EmailJS',
      [EmailProviderType.VERCEL_API]: 'Vercel Serverless API',
      [EmailProviderType.CUSTOM_BACKEND]: 'Custom Backend',
      [EmailProviderType.FORMSPREE]: 'FormSpree',
      [EmailProviderType.WEB3FORMS]: 'Web3Forms',
    };

    return {
      type: this.currentProviderType,
      name: names[this.currentProviderType] || 'Unknown',
    };
  }
}

// Singleton instance
export const emailServiceFactory = new EmailServiceFactory();

// Export conveniente
export const getEmailProvider = () => emailServiceFactory.getProvider();
export const setEmailProvider = (type: EmailProviderType) => emailServiceFactory.setProvider(type);
export const getProviderInfo = () => emailServiceFactory.getCurrentProviderInfo();
